import { useQuery } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useState, useCallback, useMemo } from "react";
import { type SortingState } from "@tanstack/react-table";
import { LeadsHeader } from "shared/app/LeadsTableView/components/LeadsHeader";
import { LeadsTable } from "shared/app/LeadsTableView/slices/LeadsTable";
import { NoLeadsSlice } from "shared/app/LeadsTableView/slices/NoLeadsSlice";
import { Pagination } from "shared/components/Pagination";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import { PaginatedResponse } from "shared/types";
import { Lead } from "views/leads-id/types";
import { fetchStages, stagesQueryKey } from "views/settings-crm/api";
import { StageKind } from "views/settings-crm/types/stage";
import queryString from "qs";
import { FiltersDrawer } from "./components/FiltersDrawers";
import { Show } from "shared/components";
import { FilterBadge } from "shared/app/LeadsTableView/components/FilterBadge";
import { CreatedWithinOption } from "./components/FiltersDrawers";
import { buildAssigneesParam } from "shared/components/Assignee";

const DEFAULT_PER_PAGE = window.localStorage.getItem("per_page") || "10";

const LEAD_SORT_COLUMN_MAP: Record<string, string> = {
  last_note: "last_note_at"
};

const DEFAULT_SORTING: SortingState = [];

const getOrderDirection = (sort?: SortingState[number]) => {
  if (!sort) return undefined;
  return sort.desc ? "desc" : "asc";
};

const CREATED_WITHIN_DAYS: Partial<Record<CreatedWithinOption, number>> = {
  [CreatedWithinOption.Last7Days]: 7,
  [CreatedWithinOption.Last30Days]: 30
};

export interface LeadsFilters {
  properties?: { id: string; name: string }[];
  stages?: { id: string; name: string }[];
  leadSources?: { id: string; name: string }[];
  activityTypes?: string[];
  createdWithin?: CreatedWithinOption;
  createdAfter?: string;
  createdBefore?: string;
}

const getCreatedWithinLabel = (filters: LeadsFilters): string => {
  if (filters.createdWithin === CreatedWithinOption.Last7Days)
    return "Last 7 days";
  if (filters.createdWithin === CreatedWithinOption.Last30Days)
    return "Last 30 days";
  const after = filters.createdAfter?.slice(5).replace("-", "/") ?? "";
  const before = filters.createdBefore?.slice(5).replace("-", "/") ?? "";
  if (filters.createdAfter === filters.createdBefore) return after;
  return `${after} - ${before}`;
};

export const LeadsTableView = ({
  hasHeaderTitle = false,
  property_ids,
  unit_ids,
  contact_id,
  viewPortHeightOffset = 120
}: {
  hasHeaderTitle?: boolean;
  property_ids?: string[];
  unit_ids?: string[];
  contact_id?: number;
  viewPortHeightOffset?: number;
}) => {
  const [isCreateLeadModalOpen, setIsCreateLeadModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParamsState(
    {
      search: "",
      page: "1",
      per_page: DEFAULT_PER_PAGE
    },
    ["search", "page", "per_page"]
  );

  // State for assignee filtering
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<
    number[] | null
  >(null);

  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);

  const [debouncedSearch] = useDebouncedValue(searchParams.search, 500);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<LeadsFilters>();

  const propertyIds = useMemo(
    () => filters?.properties?.map((p) => p.id),
    [filters?.properties]
  );
  const stageIds = useMemo(
    () => filters?.stages?.map((s) => s.id),
    [filters?.stages]
  );
  const leadSourceIds = useMemo(
    () => filters?.leadSources?.map((ls) => ls.id),
    [filters?.leadSources]
  );

  const removeFilter = useCallback(
    <K extends keyof LeadsFilters>(key: K, id: string) => {
      setFilters((prev) => {
        const arr = prev?.[key];
        if (!arr?.length) return prev;
        return {
          ...prev,
          [key]:
            key === "activityTypes"
              ? (arr as string[]).filter((item) => item !== id)
              : (arr as { id: string; name: string }[]).filter(
                  (item) => item.id !== id
                )
        };
      });
      setSearchParams({ ...searchParams, page: "1" });
    },
    [searchParams, setSearchParams]
  );

  const {
    data: queryLeadsData,
    refetch: refetchLeads,
    isFetching
  } = useQuery({
    queryKey: [
      "LEADS_TABLE",
      searchParams.page,
      debouncedSearch,
      selectedAssigneeIds,
      propertyIds,
      stageIds,
      leadSourceIds,
      contact_id,
      filters?.activityTypes,
      filters?.createdWithin,
      filters?.createdAfter,
      filters?.createdBefore,
      sorting
    ],
    queryFn: async () => {
      const createdWithinDays = filters?.createdWithin
        ? CREATED_WITHIN_DAYS[filters.createdWithin]
        : undefined;
      const createdAfter =
        filters?.createdWithin === CreatedWithinOption.Custom &&
        filters.createdAfter
          ? new Date(`${filters.createdAfter}T00:00:00`).toISOString()
          : undefined;
      const createdBefore =
        filters?.createdWithin === CreatedWithinOption.Custom &&
        filters.createdBefore
          ? new Date(`${filters.createdBefore}T23:59:59.999`).toISOString()
          : undefined;

      return await instance
        .get(
          "/portal/v1/leads?" +
            queryString.stringify(
              {
                page: searchParams.page,
                per_page: 10,
                property_ids: property_ids || propertyIds,
                unit_ids: unit_ids,
                stage_ids: stageIds?.length ? stageIds : undefined,
                source_ids: leadSourceIds?.length ? leadSourceIds : undefined,
                assignees: buildAssigneesParam(selectedAssigneeIds),
                most_recent_activity: filters?.activityTypes?.length
                  ? { type: filters.activityTypes }
                  : undefined,
                created_within_days: createdWithinDays,
                created_after: createdAfter,
                created_before: createdBefore,
                search: debouncedSearch.trim() || undefined,
                contact_id,
                order_by: sorting[0]
                  ? LEAD_SORT_COLUMN_MAP[sorting[0].id]
                  : undefined,
                order_direction: getOrderDirection(sorting[0])
              },
              {
                arrayFormat: "brackets"
              }
            )
        )
        .then((res) => res.data as PaginatedResponse<Lead>);
    }
  });

  const { data: queryStagesData } = useQuery({
    queryKey: stagesQueryKey(StageKind.Lead),
    queryFn: () => fetchStages(StageKind.Lead)
  });

  const hasActiveFilters =
    Object.values(filters ?? {}).some(
      (f) => Array.isArray(f) && f.length > 0
    ) ||
    (filters?.createdWithin != null &&
      filters.createdWithin !== CreatedWithinOption.None);
  const hasAssigneeFilter = Boolean(selectedAssigneeIds?.length);
  const hasSearch = Boolean(debouncedSearch.trim());

  if (
    queryLeadsData?.items.length === 0 &&
    !hasSearch &&
    !isFetching &&
    !hasActiveFilters &&
    !hasAssigneeFilter
  )
    return (
      <NoLeadsSlice
        hasHeaderTitle={hasHeaderTitle}
        setIsCreateLeadModalOpen={setIsCreateLeadModalOpen}
        refetchLeads={refetchLeads}
        isCreateLeadModalOpen={isCreateLeadModalOpen}
      />
    );

  return (
    <>
      <LeadsHeader
        search={searchParams.search}
        hasHeaderTitle={hasHeaderTitle}
        isCreateLeadModalOpen={isCreateLeadModalOpen}
        refetchLeads={refetchLeads}
        setIsCreateLeadModalOpen={setIsCreateLeadModalOpen}
        setSearch={(search) =>
          setSearchParams({ ...searchParams, page: "1", search: search })
        }
        selectedAssigneeIds={selectedAssigneeIds}
        setSelectedAssigneeIds={setSelectedAssigneeIds}
        onPageChange={(page) => setSearchParams({ ...searchParams, page })}
        setShowFilters={setShowFilters}
      />
      <Show if={hasActiveFilters}>
        <div className="flex flex-wrap gap-2 mb-4">
          {filters?.properties?.map((property) => (
            <FilterBadge
              key={property.id}
              filter={property.name}
              onRemove={() => removeFilter("properties", property.id)}
            />
          ))}
          {filters?.stages?.map((stage) => (
            <FilterBadge
              key={stage.id}
              filter={stage.name}
              onRemove={() => removeFilter("stages", stage.id)}
            />
          ))}
          {filters?.leadSources?.map((leadSource) => (
            <FilterBadge
              key={leadSource.id}
              filter={leadSource.name}
              onRemove={() => removeFilter("leadSources", leadSource.id)}
            />
          ))}
          {filters?.activityTypes?.map((activityType) => (
            <FilterBadge
              key={activityType}
              filter={activityType}
              onRemove={() => removeFilter("activityTypes", activityType)}
            />
          ))}
          {filters?.createdWithin &&
            filters.createdWithin !== CreatedWithinOption.None && (
              <FilterBadge
                filter={getCreatedWithinLabel(filters)}
                onRemove={() =>
                  setFilters((prev) => ({
                    ...prev,
                    createdWithin: CreatedWithinOption.None,
                    createdAfter: undefined,
                    createdBefore: undefined
                  }))
                }
              />
            )}
        </div>
      </Show>
      <LeadsTable
        stagesData={queryStagesData || []}
        isLoading={isFetching}
        data={queryLeadsData?.items || []}
        perPageRows={Number(DEFAULT_PER_PAGE)}
        viewPortHeightOffset={viewPortHeightOffset}
        sorting={sorting}
        onSortingChange={(next) => {
          setSorting(next);
          setSearchParams({ ...searchParams, page: "1" });
        }}
        pagination={
          <div className="flex items-center justify-between">
            <p className="font-medium text-content-1000 text-base">
              Total leads: {queryLeadsData?.total_count || 0}
            </p>
            <div className="ml-auto flex md:max-w-[calc(100vw-106px)]">
              <Pagination
                className="md:justify-end justify-center"
                total={queryLeadsData?.page_count || 0}
                current={Number(searchParams.page)}
                onChange={(p) =>
                  setSearchParams({ ...searchParams, page: String(p) })
                }
              />
            </div>
          </div>
        }
      />
      <FiltersDrawer
        property_ids={property_ids}
        unit_ids={unit_ids}
        filters={filters}
        setFilters={setFilters}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        onApplyFilters={() => setSearchParams({ ...searchParams, page: "1" })}
        search={debouncedSearch}
      />
    </>
  );
};
